import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
  useLegacyIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useQuery } from "@tanstack/react-query";
import { Page } from "@validereinc/common-components";
import { Insights, InsightsAdapterQueryKeys } from "@validereinc/domain";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";
import classNames from "classnames/bind";
import React, { useEffect, useRef } from "react";
import { ManageInsightsRoutePath } from "../..";
import styles from "./ManageInsightsPage.module.scss";

const cx = classNames.bind(styles);

export const ManageInsightsPage = () => {
  const {
    v2: {
      userInfo: { user },
    },
  } = useAuthenticatedContext();
  const [isInsightsTabEnabledLegacy] = useLegacyIsFeatureAvailable({
    featureFlagQuery: [FeatureFlags.LegacyInsightsDashboard],
    permissionQuery: [PermissionsMap.LegacyInsights],
    featureId: "dashboards",
  });
  const [isManageInsightsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
    permissionQuery: PermissionsMap.InsightsDashboardsWrite,
  });
  const [breadcrumbs] = useBreadcrumbsFromRoute(ManageInsightsRoutePath);
  const iframeRef = useRef(null);

  const { data: sessionData, isLoading: isLoadingSession } = useQuery({
    queryKey: InsightsAdapterQueryKeys.getInsightsSessions(),
    queryFn: () => Insights.getInsightsSessions(),
    enabled: isManageInsightsAvailable,
  });

  useEffect(() => {
    async function embedConsole() {
      if (!sessionData?.embedUrl || !iframeRef.current) {
        return;
      }

      const embeddingContext =
        await QuickSightEmbedding.createEmbeddingContext();

      const frameOptions = {
        url: sessionData.embedUrl,
        container: iframeRef.current,
        withIframePlaceholder: true,
        resizeHeightOnSizeChangedEvent: true,
      };
      await embeddingContext.embedConsole(frameOptions);
    }

    embedConsole();
  }, [sessionData?.embedUrl]);

  if (
    (!isManageInsightsAvailable && !isInsightsTabEnabledLegacy) ||
    !user?.can_access_quicksight_console
  ) {
    return <AccessDeniedLayout />;
  }

  return (
    <Page
      isLoading={isLoadingSession}
      title="Manage Insights"
      breadcrumbs={breadcrumbs}
    >
      <div className={cx("iframeContainer")}>
        <div
          style={{ height: "100%" }}
          ref={iframeRef}
        />
      </div>
    </Page>
  );
};
